// src/pages/TodaysPage.tsx - Daily overview with tabbed interface
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box } from '@mui/material';
import { useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useErrorHandler, ErrorDisplayType, ERROR_MESSAGES } from '../errors';
import { PageError } from '../components/common/PageError';
import PageTitle from '../components/common/PageTitle';
import { TodaysPageSubMenu } from '../components/menus';
import { useAuth } from '../contexts/AuthContext';
import { Permissions } from '../utils/permissions';
import TodaysCheckInsCard from '../components/cards/TodaysCheckInsCard';
import TodaysCheckOutsCard from '../components/cards/TodaysCheckOutsCard';
import TodaysActiveBookingsCard from '../components/cards/TodaysActiveBookingsCard';
import TodaysKennelsCard from '../components/cards/TodaysKennelsCard';
import TodaysOverdueCard from '../components/cards/TodaysOverdueCard';
import { bookingsAPI, DashboardStats } from '../services/api';

type TabMode =
  'check-ins' | 'check-outs' | 'active-bookings' | 'kennels' | 'overdue';

const ContentContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '0',
}));

const TodaysPage: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleError } = useErrorHandler();
  const { user, isLoading } = useAuth();

  const tabMode: TabMode = location.pathname.includes('/check-ins')
    ? 'check-ins'
    : location.pathname.includes('/check-outs')
      ? 'check-outs'
      : location.pathname.includes('/active-bookings')
        ? 'active-bookings'
        : location.pathname.includes('/overdue')
          ? 'overdue'
          : location.pathname.includes('/kennels')
            ? 'kennels'
            : 'check-ins';

  const defaultDate = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState<string>(
    searchParams.get('date') || defaultDate
  );

  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    activeBookings: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    availableKennels: 0,
    overdueInvoices: 0,
  });

  const loadDashboardStats = useCallback(
    async (date: string) => {
      setError(null);

      try {
        const stats = await bookingsAPI.getDashboardStats(date);
        setDashboardStats(stats);
      } catch (err: any) {
        setError(ERROR_MESSAGES.OPERATIONS.LOAD_FAILED("today's overview"));
        handleError(err, {
          context: 'loading overview stats',
          displayType: ErrorDisplayType.PAGE,
          showToast: false,
        });
      }
    },
    [handleError]
  );

  useEffect(() => {
    loadDashboardStats(selectedDate);
  }, [selectedDate, loadDashboardStats]);

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setSearchParams({ date: newDate });
  };

  const displayDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString(
    'en-ZA',
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  // Alongside TodaysPageSubMenu hiding the tab button, this stops a role
  // that can't view Overdue reaching it by URL. It waits for the signed-in
  // user to load first, so an allowed role opening the URL directly is not
  // bounced to /today before its role is known.
  if (tabMode === 'overdue') {
    if (isLoading) return null;
    if (!Permissions.canViewOverdueInvoices(user?.role)) {
      return <Navigate to="/today" replace />;
    }
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <PageError
          message={error}
          onRetry={() => loadDashboardStats(selectedDate)}
        />
      </Container>
    );
  }

  return (
    <>
      <PageTitle title="Today" modeText={displayDate} showSeparator={true} />

      <TodaysPageSubMenu selectedDate={selectedDate} />

      <Box>
        <ContentContainer>
          {tabMode === 'check-ins' && (
            <TodaysCheckInsCard
              date={selectedDate}
              onDateChange={handleDateChange}
            />
          )}

          {tabMode === 'check-outs' && (
            <TodaysCheckOutsCard
              date={selectedDate}
              onDateChange={handleDateChange}
            />
          )}

          {tabMode === 'active-bookings' && (
            <TodaysActiveBookingsCard
              date={selectedDate}
              onDateChange={handleDateChange}
            />
          )}

          {tabMode === 'kennels' && <TodaysKennelsCard date={selectedDate} />}

          {tabMode === 'overdue' && (
            <TodaysOverdueCard
              count={dashboardStats.overdueInvoices}
              date={selectedDate}
            />
          )}
        </ContentContainer>
      </Box>
    </>
  );
};

export default TodaysPage;
